import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { api, API } from "@/api";
import { clearImpersonation, getPortalToken, installImpersonationInterceptor, isImpersonating } from "@/lib/portalSession";
import { requestNotificationPermission, onForegroundMessage } from "@/services/firebase";
import { toast } from "@/lib/toast";
import { logger } from "@/lib/logger";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

/**
 * Determine which auth scope to boot based on current URL.
 *  - "/portal*"  → portal only   (never load admin session on a client URL)
 *  - "/admin*"   → admin only    (never load portal session on an admin URL)
 *  - "/"         → both (entry page can redirect based on whichever exists)
 * This prevents the "admin flash" when a client opens /portal in a preview
 * where an admin token may still be in localStorage.
 */
function currentAuthScope() {
  if (typeof window === "undefined") return "both";
  const p = window.location.pathname || "/";
  if (p.startsWith("/portal")) return "portal";
  if (p.startsWith("/admin")) return "admin";
  return "both";
}

async function registerPushToken(role, clientId = null) {
  try {
    const result = await requestNotificationPermission();
    if (result?.ok && result.token) {
      const r = await api.post("/push/register", { token: result.token, role, client_id: clientId });
      if (r.data?.ok) {
        logger.info(`[push] Registered for ${role}`);
      } else {
        logger.warn("[push] Backend register failed:", r.data?.error);
      }
    } else if (result && !result.ok) {
      logger.info(`[push] Skipped registration: ${result.reason}`);
    }
  } catch (e) {
    logger.warn("[push] registerPushToken error:", e?.message || e);
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [portalUser, setPortalUser] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);

  // Listen for foreground push messages
  useEffect(() => {
    const unsub = onForegroundMessage((payload) => {
      // Batch #14.60 — Payload data-only (compatible avec l'ancien format aussi)
      const title = payload.data?.title || payload.notification?.title || "Notification";
      const body = payload.data?.body || payload.notification?.body || "";
      toast.info(`${title}: ${body}`);
    });
    return () => { if (typeof unsub === "function") unsub(); };
  }, []);

  useEffect(() => {
    installImpersonationInterceptor();
    const scope = currentAuthScope();
    const adminToken = localStorage.getItem("token");
    const portalToken = getPortalToken();
    const impersonating = isImpersonating();

    // Strict role isolation based on URL to prevent cross-role flashes
    const shouldLoadAdmin = scope !== "portal" && !!adminToken;
    const shouldLoadPortal = scope !== "admin" && !!portalToken;

    const tasks = [];

    if (shouldLoadAdmin) {
      tasks.push(
        api.get("/auth/me")
          .then((res) => { setUser(res.data); registerPushToken("admin"); })
          .catch(() => localStorage.removeItem("token"))
      );
    } else if (scope === "portal" && adminToken) {
      // Do not touch storage — admin remains logged in on /admin in another tab.
      // Just ignore admin session on /portal routes.
    }

    if (shouldLoadPortal) {
      tasks.push(
        api.get("/portal/profile", { headers: { Authorization: `Bearer ${portalToken}` } })
          .then((res) => {
            setPortalUser(res.data);
            if (impersonating) {
              // Batch #14.338 — session « en tant que » : rien dans localStorage (propre à l'onglet), jamais de changement de mot de passe forcé
              setMustChangePassword(false);
              return;
            }
            localStorage.setItem("portalUser", JSON.stringify(res.data));
            const savedMustChange = localStorage.getItem("mustChangePassword");
            setMustChangePassword(savedMustChange === "true");
          })
          .catch((e) => {
            if (impersonating) {
              // Session « en tant que » refusée (expirée, client désactivé, jeton invalide) : page explicite plutôt qu'une boucle vers le login
              clearImpersonation();
              const detail = `${e?.response?.status || ""} ${e?.response?.data?.detail || e?.message || "Jeton refusé"}`.trim();
              if (window.location.pathname !== "/portal/as/failed") window.location.replace(`/portal/as/failed?d=${encodeURIComponent(detail)}`);
              return;
            }
            localStorage.removeItem("portalToken");
            localStorage.removeItem("portalUser");
            localStorage.removeItem("mustChangePassword");
          })
      );
    }

    if (tasks.length === 0) {
      setLoading(false);
    } else {
      Promise.allSettled(tasks).finally(() => setLoading(false));
    }
  }, []);

  // Batch #14.338 — recharge la session portail à partir du jeton courant de l'onglet (après installation d'un jeton
  // « en tant que », sans rechargement de page). Retourne {ok, detail}.
  const refreshPortalSession = async () => {
    const t = getPortalToken();
    if (!t) return { ok: false, detail: "Aucun jeton" };
    try {
      const res = await api.get("/portal/profile", { headers: { Authorization: `Bearer ${t}` } });
      setPortalUser(res.data);
      setMustChangePassword(false);
      return { ok: true };
    } catch (e) {
      clearImpersonation();
      setPortalUser(null);
      return { ok: false, detail: `${e?.response?.status || ""} ${e?.response?.data?.detail || e?.message || "Jeton refusé"}`.trim() };
    }
  };

  // Finalise une session admin à partir d'une réponse /auth/login ou /auth/2fa/verify
  const completeAdminLogin = (data) => {
    localStorage.setItem("token", data.access_token);
    // Batch #14.171 — Stocker aussi le refresh token (admin uniquement)
    if (data.refresh_token) {
      localStorage.setItem("adminRefreshToken", data.refresh_token);
    }
    setUser(data.admin);
    registerPushToken("admin");
    return data;
  };

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    return completeAdminLogin(res.data);
  };

  const register = async (email, password, name) => {
    const res = await api.post("/auth/register", { email, password, name });
    return res.data;
  };

  const portalLogin = async (username, password) => {
    const res = await api.post("/portal/login", { username, password });
    localStorage.setItem("portalToken", res.data.access_token);
    localStorage.setItem("portalUser", JSON.stringify(res.data.client));
    localStorage.setItem("mustChangePassword", res.data.must_change_password ? "true" : "false");
    setPortalUser(res.data.client);
    setMustChangePassword(res.data.must_change_password || false);
    registerPushToken("portal", res.data.client?.id);
    return res.data;
  };

  const clearMustChangePassword = (newToken) => {
    if (newToken) {
      localStorage.setItem("portalToken", newToken);
    }
    localStorage.setItem("mustChangePassword", "false");
    setMustChangePassword(false);
  };

  const logout = () => {
    // Only clear the scope currently active to preserve the other session
    // (e.g. logging out from /admin should not kick out a client tab).
    const scope = currentAuthScope();
    if (scope !== "portal") {
      // Batch #14.288 — le refresh token est révoqué côté serveur (sa famille entière)
      const rt = localStorage.getItem("adminRefreshToken");
      localStorage.removeItem("token");
      localStorage.removeItem("adminRefreshToken");
      axios.post(`${API}/auth/logout`, rt ? { refresh_token: rt } : {}).catch(() => {});
      setUser(null);
    }
    if (scope !== "admin") {
      localStorage.removeItem("portalToken");
      localStorage.removeItem("portalUser");
      localStorage.removeItem("mustChangePassword");
      axios.post(`${API}/portal/logout`, {}).catch(() => {});
      setPortalUser(null);
      setMustChangePassword(false);
    }
    localStorage.removeItem("currentClientId");
  };

  /** Explicit per-scope logout. Useful for the entry page switcher. */
  const logoutAdmin = () => {
    const rt = localStorage.getItem("adminRefreshToken");
    localStorage.removeItem("token");
    localStorage.removeItem("adminRefreshToken");
    axios.post(`${API}/auth/logout`, rt ? { refresh_token: rt } : {}).catch(() => {});
    setUser(null);
  };
  /** Batch #14.288 — Déconnecte toutes les sessions (tous les appareils), puis la session courante. */
  const logoutAllSessions = async () => {
    await api.post("/auth/logout-all");
    logoutAdmin();
  };
  const logoutPortal = () => {
    if (isImpersonating()) {
      // Fin de la navigation « en tant que » : clôt la session côté serveur (journal) puis vide l'onglet.
      // Retourne la promesse : le bandeau attend la clôture AVANT de fermer l'onglet (sinon la requête est annulée).
      const t = getPortalToken();
      const done = axios.post(`${API}/portal/impersonate/end`, {}, { headers: { Authorization: `Bearer ${t}` }, timeout: 4000 }).catch(() => {});
      clearImpersonation();
      setPortalUser(null);
      setMustChangePassword(false);
      return done;
    }
    localStorage.removeItem("portalToken");
    localStorage.removeItem("portalUser");
    localStorage.removeItem("mustChangePassword");
    axios.post(`${API}/portal/logout`, {}).catch(() => {});
    setPortalUser(null);
    setMustChangePassword(false);
    return Promise.resolve();
  };

  return (
    <AuthContext.Provider value={{
      user, portalUser, mustChangePassword,
      login, completeAdminLogin, refreshPortalSession, register, portalLogin, logout, logoutAdmin, logoutPortal, logoutAllSessions,
      clearMustChangePassword, loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};
